/**
 * Forked from fastembed-js (https://github.com/Anush008/fastembed-js)
 * Original author: Anush008 <anushshetty90@gmail.com>
 * License: MIT (see LICENSE-fastembed in this package)
 *
 * The upstream project has been archived / abandoned. This fork is maintained
 * as part of the Mastra monorepo so that @mastra/fastembed no longer depends
 * on the unmaintained npm `fastembed` package.
 */

import { AddedToken, Tokenizer } from '@anush008/tokenizers';
import fs from 'node:fs';
import type { PathLike } from 'node:fs';
import path from 'node:path';
import * as ort from 'onnxruntime-node';
import { downloadFileToCacheDir } from '@huggingface/hub';

export enum ExecutionProvider {
  CPU = 'cpu',
  CUDA = 'cuda',
  WebGL = 'webgl',
  WASM = 'wasm',
  XNNPACK = 'xnnpack',
}

export enum EmbeddingModel {
  AllMiniLML6V2 = 'fast-all-MiniLM-L6-v2',
  BGEBaseEN = 'fast-bge-base-en',
  BGEBaseENV15 = 'fast-bge-base-en-v1.5',
  BGESmallEN = 'fast-bge-small-en',
  BGESmallENV15 = 'fast-bge-small-en-v1.5',
  BGESmallZH = 'fast-bge-small-zh-v1.5',
  MLE5Large = 'fast-multilingual-e5-large',
  CUSTOM = 'custom',
}

const COMMON_MODEL_FILES = ['config.json', 'tokenizer.json', 'tokenizer_config.json', 'special_tokens_map.json'];

const HF_MODEL_SOURCES: Record<Exclude<EmbeddingModel, EmbeddingModel.CUSTOM>, { repo: string; files: string[] }> = {
  [EmbeddingModel.AllMiniLML6V2]: { repo: 'Qdrant/all-MiniLM-L6-v2-onnx', files: ['model.onnx'] },
  [EmbeddingModel.BGEBaseEN]: { repo: 'Qdrant/fast-bge-base-en', files: ['model_optimized.onnx'] },
  [EmbeddingModel.BGEBaseENV15]: { repo: 'Qdrant/bge-base-en-v1.5-onnx-Q', files: ['model_optimized.onnx'] },
  [EmbeddingModel.BGESmallEN]: { repo: 'Qdrant/bge-small-en', files: ['model_optimized.onnx'] },
  [EmbeddingModel.BGESmallENV15]: { repo: 'Qdrant/bge-small-en-v1.5-onnx-Q', files: ['model_optimized.onnx'] },
  [EmbeddingModel.BGESmallZH]: { repo: 'Qdrant/bge-small-zh-v1.5', files: ['model_optimized.onnx'] },
  [EmbeddingModel.MLE5Large]: { repo: 'Qdrant/multilingual-e5-large-onnx', files: ['model.onnx', 'model.onnx_data'] },
};

export enum SparseEmbeddingModel {
  SpladePPEnV1 = 'prithivida/Splade_PP_en_v1',
  CUSTOM = 'custom',
}

export type SparseVector = {
  values: number[];
  indices: number[];
};

export interface InitOptionsBase {
  executionProviders?: ExecutionProvider[];
  maxLength?: number;
  cacheDir?: string;
  showDownloadProgress?: boolean;
}

interface ModelInfo {
  model: EmbeddingModel;
  dim: number;
  description: string;
}

interface SparseModelInfo {
  model: SparseEmbeddingModel;
  vocabSize: number;
  description: string;
}

type ModelInput = Record<string, ort.Tensor>;

function normalize(v: number[]): number[] {
  const norm = Math.sqrt(v.reduce((acc, val) => acc + val * val, 0));
  const epsilon = 1e-12;
  return v.map(val => val / Math.max(norm, epsilon));
}

function getEmbeddings(data: number[], dimensions: [number, number, number]): number[][] {
  const [x, _y, z] = dimensions;
  return new Array(x).fill(undefined).map((_, index) => {
    const startIndex = index * _y * z;
    const endIndex = startIndex + z;
    return data.slice(startIndex, endIndex);
  });
}

export interface InitStandardOptions extends InitOptionsBase {
  model: Exclude<EmbeddingModel, EmbeddingModel.CUSTOM>;
  modelAbsoluteDirPath?: undefined;
  modelName?: string;
}

export interface InitCustomOptions extends InitOptionsBase {
  model: EmbeddingModel.CUSTOM;
  modelAbsoluteDirPath: PathLike;
  modelName: string;
}

export type InitOptions = InitStandardOptions | InitCustomOptions;

export interface InitSparseStandardOptions extends InitOptionsBase {
  model: Exclude<SparseEmbeddingModel, SparseEmbeddingModel.CUSTOM>;
  modelAbsoluteDirPath?: undefined;
  modelName?: string;
}

export interface InitSparseCustomOptions extends InitOptionsBase {
  model: SparseEmbeddingModel.CUSTOM;
  modelAbsoluteDirPath: PathLike;
  modelName: string;
}

export type InitSparseOptions = InitSparseStandardOptions | InitSparseCustomOptions;

abstract class Embedding {
  abstract listSupportedModels(): ModelInfo[];
  abstract embed(texts: string[], batchSize?: number): AsyncGenerator<number[][], void, unknown>;
  abstract passageEmbed(texts: string[], batchSize: number): AsyncGenerator<number[][], void, unknown>;
  abstract queryEmbed(query: string): Promise<number[]>;
}

abstract class SparseEmbedding {
  abstract listSupportedModels(): SparseModelInfo[];
  abstract embed(texts: string[], batchSize?: number): AsyncGenerator<SparseVector[], void, unknown>;
  abstract passageEmbed(texts: string[], batchSize: number): AsyncGenerator<SparseVector[], void, unknown>;
  abstract queryEmbed(query: string): Promise<SparseVector>;
}

interface AddedTokenMap {
  content: string;
  single_word: boolean;
  lstrip: boolean;
  rstrip: boolean;
  normalized: boolean;
}

function isAddedTokenMap(token: unknown): token is AddedTokenMap {
  return (
    typeof token === 'object' &&
    token !== null &&
    'content' in token &&
    'single_word' in token &&
    'rstrip' in token &&
    'lstrip' in token &&
    'normalized' in token
  );
}

function loadTokenizerFromDir(modelDir: PathLike, maxLength: number): Tokenizer {
  const tokenizerPath = path.join(modelDir.toString(), 'tokenizer.json');
  if (!fs.existsSync(tokenizerPath)) {
    throw new Error(`Tokenizer file not found at ${tokenizerPath}`);
  }

  const configPath = path.join(modelDir.toString(), 'config.json');
  if (!fs.existsSync(configPath)) {
    throw new Error(`Config file not found at ${configPath}`);
  }
  const config = JSON.parse(fs.readFileSync(configPath, 'utf-8'));

  const tokenizerFilePath = path.join(modelDir.toString(), 'tokenizer_config.json');
  if (!fs.existsSync(tokenizerFilePath)) {
    throw new Error(`Tokenizer config file not found at ${tokenizerFilePath}`);
  }
  const tokenizerConfig = JSON.parse(fs.readFileSync(tokenizerFilePath, 'utf-8'));
  maxLength = Math.min(maxLength, tokenizerConfig['model_max_length']);

  const tokensMapPath = path.join(modelDir.toString(), 'special_tokens_map.json');
  if (!fs.existsSync(tokensMapPath)) {
    throw new Error(`Tokens map file not found at ${tokensMapPath}`);
  }
  const tokensMap = JSON.parse(fs.readFileSync(tokensMapPath, 'utf-8'));

  const tokenizer = Tokenizer.fromFile(tokenizerPath);

  tokenizer.setTruncation(maxLength);
  tokenizer.setPadding({
    maxLength,
    padId: config['pad_token_id'],
    padToken: tokenizerConfig['pad_token'],
  });

  for (const token of Object.values(tokensMap)) {
    if (typeof token === 'string') {
      tokenizer.addSpecialTokens([token]);
    } else if (isAddedTokenMap(token)) {
      const addedToken = new AddedToken(token['content'], true, {
        singleWord: token['single_word'],
        leftStrip: token['lstrip'],
        rightStrip: token['rstrip'],
        normalized: token['normalized'],
      });
      tokenizer.addAddedTokens([addedToken]);
    }
  }
  return tokenizer;
}

export class FlagEmbedding extends Embedding {
  private constructor(
    private tokenizer: Tokenizer,
    private session: ort.InferenceSession,
    private model: EmbeddingModel,
  ) {
    super();
  }

  static async init(options: InitStandardOptions): Promise<FlagEmbedding>;
  static async init(options: InitCustomOptions): Promise<FlagEmbedding>;
  static async init({
    model = EmbeddingModel.BGESmallENV15,
    executionProviders = [ExecutionProvider.CPU],
    maxLength = 512,
    cacheDir = 'local_cache',
    showDownloadProgress = true,
    modelAbsoluteDirPath = '',
    modelName = '',
  }: Partial<InitOptions> = {}) {
    if (model === EmbeddingModel.CUSTOM) {
      if (!modelAbsoluteDirPath) {
        throw new Error('For custom model, modelAbsoluteDirPath is required in FlagEmbedding.init');
      }
      if (!modelName) {
        throw new Error('For custom model, modelName is required in FlagEmbedding.init');
      }
    }

    const modelDir =
      model === EmbeddingModel.CUSTOM
        ? modelAbsoluteDirPath
        : await FlagEmbedding.retrieveModel(model, cacheDir, showDownloadProgress);

    const tokenizer = loadTokenizerFromDir(modelDir, maxLength);
    const defaultModelName =
      model === EmbeddingModel.MLE5Large || model === EmbeddingModel.AllMiniLML6V2
        ? 'model.onnx'
        : 'model_optimized.onnx';
    const modelPath = path.join(modelDir.toString(), modelName || defaultModelName);
    if (!fs.existsSync(modelPath)) {
      throw new Error(`Model file not found at ${modelPath}`);
    }
    const session = await ort.InferenceSession.create(modelPath, {
      executionProviders,
      graphOptimizationLevel: 'all',
    });
    return new FlagEmbedding(tokenizer, session, model);
  }

  static async retrieveModel(
    model: EmbeddingModel,
    cacheDir: PathLike,
    showDownloadProgress: boolean = true,
  ): Promise<PathLike> {
    if (model === EmbeddingModel.CUSTOM) {
      throw new Error('Custom models must be loaded from modelAbsoluteDirPath');
    }
    if (!fs.existsSync(cacheDir)) {
      fs.mkdirSync(cacheDir, { recursive: true, mode: 0o755 });
    }

    const modelDir = path.join(cacheDir.toString(), model);
    if (fs.existsSync(modelDir)) {
      return modelDir;
    }

    const { repo, files } = HF_MODEL_SOURCES[model];
    const tmpDir = fs.mkdtempSync(`${modelDir}.tmp-`);
    // Keep the Hugging Face hub cache inside the configured cacheDir (not ~/.cache) and
    // move blobs out of it so each model file is stored only once.
    const hubCacheDir = path.join(tmpDir, '.hf-hub');

    try {
      if (showDownloadProgress) {
        console.info(`Downloading ${model} from Hugging Face (${repo})...`);
      }
      for (const file of [...COMMON_MODEL_FILES, ...files]) {
        const downloaded = await downloadFileToCacheDir({ repo, path: file, cacheDir: hubCacheDir });
        fs.renameSync(fs.realpathSync(downloaded), path.join(tmpDir, file));
      }
      fs.rmSync(hubCacheDir, { recursive: true, force: true });
      try {
        fs.renameSync(tmpDir, modelDir);
      } catch (error) {
        const code = (error as NodeJS.ErrnoException).code;
        // Another concurrent download already published this model.
        if ((code === 'EEXIST' || code === 'ENOTEMPTY') && fs.existsSync(modelDir)) {
          fs.rmSync(tmpDir, { recursive: true, force: true });
          return modelDir;
        }
        throw error;
      }
    } catch (error) {
      fs.rmSync(tmpDir, { recursive: true, force: true });
      throw error;
    }
    return modelDir;
  }

  async *embed(textStrings: string[], batchSize: number = 256) {
    for (let i = 0; i < textStrings.length; i += batchSize) {
      const batchTexts = textStrings.slice(i, i + batchSize);

      const encodedTexts = await Promise.all(batchTexts.map(textString => this.tokenizer.encode(textString)));

      const idsArray: bigint[][] = [];
      const maskArray: bigint[][] = [];
      const typeIdsArray: bigint[][] = [];

      encodedTexts.forEach(text => {
        const ids = text.getIds().map(BigInt);
        const mask = text.getAttentionMask().map(BigInt);
        const typeIds = text.getTypeIds().map(BigInt);

        idsArray.push(ids);
        maskArray.push(mask);
        typeIdsArray.push(typeIds);
      });

      const maxLength = idsArray[0]!.length;

      const batchInputIds = new ort.Tensor('int64', idsArray.flat() as unknown as number[], [
        batchTexts.length,
        maxLength,
      ]);
      const batchAttentionMask = new ort.Tensor('int64', maskArray.flat() as unknown as number[], [
        batchTexts.length,
        maxLength,
      ]);
      const batchTokenTypeId = new ort.Tensor('int64', typeIdsArray.flat() as unknown as number[], [
        batchTexts.length,
        maxLength,
      ]);

      const inputs: ModelInput = {
        input_ids: batchInputIds,
        attention_mask: batchAttentionMask,
        token_type_ids: batchTokenTypeId,
      };

      if (this.model === EmbeddingModel.MLE5Large) {
        delete inputs.token_type_ids;
      }

      const output = await this.session.run(inputs);

      const lastHiddenState = output.last_hidden_state!;
      const embeddings = getEmbeddings(
        lastHiddenState.data as unknown[] as number[],
        lastHiddenState.dims as [number, number, number],
      );

      yield embeddings.map(normalize);
    }
  }

  passageEmbed(texts: string[], batchSize: number = 256) {
    texts = texts.map(text => `passage: ${text}`);
    return this.embed(texts, batchSize);
  }

  async queryEmbed(query: string): Promise<number[]> {
    const result = await this.embed([`query: ${query}`]).next();
    return result.value![0]!;
  }

  listSupportedModels(): ModelInfo[] {
    return [
      { model: EmbeddingModel.BGESmallEN, dim: 384, description: 'Fast English model' },
      { model: EmbeddingModel.BGESmallENV15, dim: 384, description: 'v1.5 release of the fast, default English model' },
      { model: EmbeddingModel.BGEBaseEN, dim: 768, description: 'Base English model' },
      { model: EmbeddingModel.BGEBaseENV15, dim: 768, description: 'v1.5 release of Base English model' },
      { model: EmbeddingModel.BGESmallZH, dim: 512, description: 'v1.5 release of the fast, Chinese model' },
      { model: EmbeddingModel.AllMiniLML6V2, dim: 384, description: 'Sentence Transformer model, MiniLM-L6-v2' },
      {
        model: EmbeddingModel.MLE5Large,
        dim: 1024,
        description: 'Multilingual model, e5-large. Recommend using this model for non-English languages',
      },
    ];
  }
}

export class SparseTextEmbedding extends SparseEmbedding {
  private constructor(
    private tokenizer: Tokenizer,
    private session: ort.InferenceSession,
  ) {
    super();
  }

  static async init(options: InitSparseStandardOptions): Promise<SparseTextEmbedding>;
  static async init(options: InitSparseCustomOptions): Promise<SparseTextEmbedding>;
  static async init({
    model = SparseEmbeddingModel.SpladePPEnV1,
    executionProviders = [ExecutionProvider.CPU],
    maxLength = 512,
    cacheDir = 'local_cache',
    showDownloadProgress = true,
    modelAbsoluteDirPath = '',
    modelName = '',
  }: Partial<InitSparseOptions> = {}) {
    if (model === SparseEmbeddingModel.CUSTOM) {
      if (!modelAbsoluteDirPath) {
        throw new Error('For custom model, modelAbsoluteDirPath is required in SparseTextEmbedding.init');
      }
      if (!modelName) {
        throw new Error('For custom model, modelName is required in SparseTextEmbedding.init');
      }
    }

    const modelDir =
      model === SparseEmbeddingModel.CUSTOM
        ? modelAbsoluteDirPath
        : await SparseTextEmbedding.retrieveModel(model, cacheDir, showDownloadProgress);

    const { tokenizer } = this.loadTokenizer(modelDir, maxLength);

    const defaultModelName = 'model.onnx';
    const modelPath = path.join(modelDir.toString(), 'onnx', modelName || defaultModelName);

    if (!fs.existsSync(modelPath)) {
      throw new Error(`Model file not found at ${modelPath}`);
    }

    const session = await ort.InferenceSession.create(modelPath, {
      executionProviders,
      graphOptimizationLevel: 'all',
    });

    return new SparseTextEmbedding(tokenizer, session);
  }

  private static loadTokenizer(modelDir: PathLike, maxLength: number): { tokenizer: Tokenizer } {
    const tokenizer = loadTokenizerFromDir(modelDir, maxLength);
    return { tokenizer };
  }

  private static async retrieveModel(
    model: SparseEmbeddingModel,
    cacheDir: PathLike,
    _showDownloadProgress: boolean = true,
  ): Promise<PathLike> {
    if (!fs.existsSync(cacheDir)) {
      fs.mkdirSync(cacheDir, { mode: 0o755 });
    }

    const modelDir = path.join(cacheDir.toString(), model.replace('/', '_'));

    if (fs.existsSync(modelDir)) {
      return modelDir;
    }

    fs.mkdirSync(modelDir, { mode: 0o755 });

    const filesToDownload = [
      'onnx/model.onnx',
      'tokenizer.json',
      'tokenizer_config.json',
      'config.json',
      'special_tokens_map.json',
    ];

    for (const fileName of filesToDownload) {
      const outputPath = path.join(modelDir, fileName);
      const outputDir = path.dirname(outputPath);

      if (!fs.existsSync(outputDir)) {
        fs.mkdirSync(outputDir, { recursive: true, mode: 0o755 });
      }

      const downloaded = await downloadFileToCacheDir({
        repo: model,
        path: fileName,
      });

      if (downloaded && typeof downloaded === 'string') {
        fs.copyFileSync(downloaded, outputPath);
      }
    }

    return modelDir;
  }

  async *embed(textStrings: string[], batchSize: number = 256) {
    for (let i = 0; i < textStrings.length; i += batchSize) {
      const batchTexts = textStrings.slice(i, i + batchSize);

      const encodedTexts = await Promise.all(batchTexts.map(textString => this.tokenizer.encode(textString)));

      const idsArray: bigint[][] = [];
      const maskArray: number[][] = [];
      const typeIdsArray: bigint[][] = [];

      encodedTexts.forEach(text => {
        const ids = text.getIds().map(BigInt);
        const mask = text.getAttentionMask();
        const typeIds = text.getTypeIds().map(BigInt);

        idsArray.push(ids);
        maskArray.push(mask);
        typeIdsArray.push(typeIds);
      });

      const maxLength = idsArray[0]!.length;

      const batchInputIds = new ort.Tensor('int64', idsArray.flat() as unknown as number[], [
        batchTexts.length,
        maxLength,
      ]);
      const batchAttentionMask = new ort.Tensor('int64', maskArray.flat().map(BigInt) as unknown as number[], [
        batchTexts.length,
        maxLength,
      ]);
      const batchTokenTypeId = new ort.Tensor('int64', typeIdsArray.flat() as unknown as number[], [
        batchTexts.length,
        maxLength,
      ]);

      const inputs: ModelInput = {
        input_ids: batchInputIds,
        input_mask: batchAttentionMask,
        segment_ids: batchTokenTypeId,
      };

      const output = await this.session.run(inputs);

      // SPLADE postprocessing: log(1 + ReLU(logits))
      const outputTensor = output.output!;
      // @ts-expect-error cpuData exists at runtime on onnxruntime Tensor
      const logits = outputTensor.cpuData as Float32Array;
      const dims = outputTensor.dims as [number, number, number];
      const [currentBatchSize, seqLen, currentVocabSize] = dims;

      const sparseVectors: SparseVector[] = [];

      for (let batchIdx = 0; batchIdx < currentBatchSize; batchIdx++) {
        const valuesArr = new Float32Array(currentVocabSize).fill(0);

        for (let seqIdx = 0; seqIdx < seqLen; seqIdx++) {
          const attentionValue = maskArray[batchIdx]![seqIdx]!;

          if (attentionValue > 0) {
            for (let vocabIdx = 0; vocabIdx < currentVocabSize; vocabIdx++) {
              const logitIdx = batchIdx * seqLen * currentVocabSize + seqIdx * currentVocabSize + vocabIdx;
              const logitValue = logits[logitIdx]!;
              const reluValue = Math.max(0, logitValue);
              const logValue = Math.log(1 + reluValue);
              valuesArr[vocabIdx] = Math.max(valuesArr[vocabIdx]!, logValue);
            }
          }
        }

        const sparseVector: SparseVector = { values: [], indices: [] };
        for (let tokenId = 0; tokenId < currentVocabSize; tokenId++) {
          if (valuesArr[tokenId]! > 0) {
            sparseVector.indices.push(tokenId);
            sparseVector.values.push(valuesArr[tokenId]!);
          }
        }
        sparseVectors.push(sparseVector);
      }

      yield sparseVectors;
    }
  }

  passageEmbed(texts: string[], batchSize: number = 256) {
    return this.embed(texts, batchSize);
  }

  async queryEmbed(query: string): Promise<SparseVector> {
    const result = await this.embed([query]).next();
    return result.value![0]!;
  }

  listSupportedModels(): SparseModelInfo[] {
    return [
      {
        model: SparseEmbeddingModel.SpladePPEnV1,
        vocabSize: 30522,
        description: 'SPLADE++ English model for sparse retrieval',
      },
    ];
  }
}
